/**
 * @fileoverview Service for document verification
 */
import { documentService } from './documentService';
// import { contractService } from '@/services/web3/contractService';
class VerificationService {
    /**
     * Verify document by hash or document ID
     */
    async verifyDocument(hashOrId) {
        const result = {
            isValid: false,
            document: null,
            checks: {
                documentExists: false,
                blockchain: {
                    verified: false,
                    hash: '',
                    message: '',
                },
                ipfs: {
                    verified: false,
                    hash: '',
                    message: '',
                },
                issuer: {
                    verified: false,
                    issuerName: '',
                    issuerId: '',
                    message: '',
                },
                integrity: {
                    verified: false,
                    message: '',
                },
                timestamp: {
                    verified: false,
                    issuedAt: null,
                    message: '',
                },
            },
            errors: [],
        };
        try {
            // Find document by ID or hash
            let document = null;
            // Try to find by ID first
            document = documentService.getDocumentById(hashOrId);
            // If not found, try to find by blockchain hash
            if (!document) {
                const allDocuments = documentService.getDocuments();
                document = allDocuments.find(d => d.blockchainHash === hashOrId || d.id === hashOrId) || null;
            }
            if (!document) {
                result.errors.push('Documento no encontrado');
                result.checks.documentExists = false;
                return result;
            }
            result.document = document;
            result.checks.documentExists = true;
            // Verify Blockchain
            try {
                const blockchainVerified = await this.verifyBlockchain(document.blockchainHash);
                result.checks.blockchain = {
                    verified: blockchainVerified,
                    hash: document.blockchainHash,
                    message: blockchainVerified
                        ? 'Hash verificado en blockchain'
                        : 'No se pudo verificar en blockchain',
                };
            }
            catch (error) {
                result.checks.blockchain = {
                    verified: false,
                    hash: document.blockchainHash,
                    message: `Error verificando blockchain: ${error.message}`,
                };
                result.errors.push(`Blockchain: ${error.message}`);
            }
            // Verify IPFS
            try {
                const ipfsVerified = await this.verifyIPFS(document.ipfsHash);
                result.checks.ipfs = {
                    verified: ipfsVerified,
                    hash: document.ipfsHash,
                    message: ipfsVerified
                        ? 'Contenido verificado en IPFS'
                        : 'No se pudo verificar en IPFS',
                };
            }
            catch (error) {
                result.checks.ipfs = {
                    verified: false,
                    hash: document.ipfsHash,
                    message: `Error verificando IPFS: ${error.message}`,
                };
                result.errors.push(`IPFS: ${error.message}`);
            }
            // Verify Issuer
            result.checks.issuer = {
                verified: !!document.issuerId && !!document.issuerName,
                issuerName: document.issuerName,
                issuerId: document.issuerId,
                message: document.issuerName
                    ? `Emitido por: ${document.issuerName}`
                    : 'Emisor no identificado',
            };
            // Verify Integrity
            const integrityVerified = this.verifyIntegrity(document);
            result.checks.integrity = {
                verified: integrityVerified,
                message: integrityVerified
                    ? 'Integridad del documento verificada'
                    : 'La integridad del documento no pudo ser verificada',
            };
            // Verify Timestamp
            result.checks.timestamp = {
                verified: !!document.issuedAt,
                issuedAt: document.issuedAt,
                message: document.issuedAt
                    ? `Emitido el: ${new Date(document.issuedAt).toLocaleString()}`
                    : 'Fecha de emisión no disponible',
            };
            // Overall validation
            result.isValid =
                result.checks.documentExists &&
                result.checks.blockchain.verified &&
                result.checks.ipfs.verified &&
                result.checks.issuer.verified &&
                result.checks.integrity.verified &&
                result.checks.timestamp.verified;
        }
        catch (error) {
            result.errors.push(`Error general: ${error.message}`);
        }
        return result;
    }
    /**
     * Verify document on blockchain
     */
    async verifyBlockchain(hash) {
        try {
            // Client-side verification removed.
            // In a real scenario, this would check against a public explorer API or backend.
            return !!hash && hash.length > 0;
        }
        catch (error) {
            console.error('Blockchain verification error:', error);
            return false;
        }
    }
    /**
     * Verify document on IPFS
     */
    async verifyIPFS(ipfsHash) {
        try {
            // Try to verify using IPFS service
            // This is a placeholder - actual implementation depends on IPFS setup
            if (ipfsHash && ipfsHash.length > 0) {
                // In production, verify that content exists on IPFS
                return true;
            }
            return false;
        }
        catch (error) {
            console.error('IPFS verification error:', error);
            return false;
        }
    }
    /**
     * Verify document integrity
     */
    verifyIntegrity(document) {
        try {
            // Verify that all required fields are present
            const hasRequiredFields = !!document.id &&
                !!document.blockchainHash &&
                !!document.ipfsHash &&
                !!document.issuerId &&
                !!document.recipientEmail &&
                !!document.issuedAt;
            // Verify hash consistency
            const hashConsistent = document.blockchainHash.length > 0 &&
                document.ipfsHash.length > 0;
            return hasRequiredFields && hashConsistent;
        }
        catch (error) {
            console.error('Integrity verification error:', error);
            return false;
        }
    }
    /**
     * Extract document ID or hash from QR code data
     */
    extractHashFromQR(qrData) {
        try {
            // QR code might contain URL or direct hash
            // Try to extract hash from URL pattern
            const urlMatch = qrData.match(/\/document\/([a-zA-Z0-9_-]+)/);
            if (urlMatch) {
                return urlMatch[1];
            }
            // Try to extract hash directly
            const hashMatch = qrData.match(/([a-zA-Z0-9]{20,})/);
            if (hashMatch) {
                return hashMatch[1];
            }
            // Return as-is if it looks like a hash
            if (qrData.length > 10) {
                return qrData;
            }
            return null;
        }
        catch (error) {
            console.error('Error extracting hash from QR:', error);
            return null;
        }
    }
}
export const verificationService = new VerificationService();


